import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const KEY = "dd_token";
const IDLE_LIMIT_MS = 30 * 60 * 1000;      // 30 minutes of inactivity → sign out
const IDLE_WARN_MS = 29 * 60 * 1000;       // 1-minute warning before the hammer drops

const AuthCtx = createContext(null);

const client = axios.create({ baseURL: API });

// Attach token to every request
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Also patch the global axios so the existing api.js requests carry the token
axios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(KEY);
  if (t && !cfg.headers?.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const authClient = client;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const idleTimer = useRef(null);
  const idleWarnTimer = useRef(null);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const s = await client.get("/auth/setup-status");
      setSetupRequired(Boolean(s.data?.setup_required));
    } catch { /* backend down — surface at login */ }
    const t = localStorage.getItem(KEY);
    if (t) {
      try {
        const r = await client.get("/auth/me");
        setUser(r.data);
      } catch {
        localStorage.removeItem(KEY);
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Login now returns one of THREE possible shapes:
  //   1. { access_token, refresh_token, user } — fully signed in
  //   2. { two_factor_required: true, challenge_token, user_email } — needs TOTP challenge
  //   3. { two_factor_setup_required: true, setup_token, user_email, role } — needs enrolment
  const login = async (email, password) => {
    const r = await client.post("/auth/login", { email, password });
    if (r.data?.access_token) {
      localStorage.setItem(KEY, r.data.access_token);
      setUser(r.data.user);
      return { kind: "signed-in", user: r.data.user };
    }
    if (r.data?.two_factor_required) {
      return {
        kind: "two-factor-required",
        challenge_token: r.data.challenge_token,
        user_email: r.data.user_email,
        method: r.data.method || "totp",
        email_sent: r.data.email_sent,
        email_error: r.data.email_error,
      };
    }
    if (r.data?.two_factor_setup_required) {
      return {
        kind: "two-factor-setup-required",
        setup_token: r.data.setup_token,
        user_email: r.data.user_email,
        role: r.data.role,
      };
    }
    throw new Error("Unexpected login response");
  };

  const submitTwoFactorChallenge = async (challenge_token, code) => {
    const r = await client.post("/auth/2fa/challenge", { challenge_token, code });
    localStorage.setItem(KEY, r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const startTwoFactorSetup = async (setup_token) => {
    const r = await client.post("/auth/2fa/setup", { setup_token });
    return r.data;
  };

  const verifyTwoFactorSetup = async (setup_token, code) => {
    const r = await client.post("/auth/2fa/verify-setup", { setup_token, code });
    localStorage.setItem(KEY, r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const setup = async (payload) => {
    const r = await client.post("/auth/setup", payload);
    localStorage.setItem(KEY, r.data.access_token);
    setUser(r.data.user);
    setSetupRequired(false);
    return r.data.user;
  };

  const logout = useCallback(async () => {
    try { await client.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(KEY);
    setUser(null);
  }, []);

  // ---- 30-minute idle sign-out ----------------------------------
  // Any user interaction (mouse, keyboard, scroll, touch) resets the timer.
  // A one-minute warning toast fires at 29 min, then hard sign-out at 30.
  const resetIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (idleWarnTimer.current) clearTimeout(idleWarnTimer.current);
    if (!user) return;
    idleWarnTimer.current = setTimeout(() => {
      toast.message("You'll be signed out in 1 minute due to inactivity.", {
        duration: 60000,
      });
    }, IDLE_WARN_MS);
    idleTimer.current = setTimeout(async () => {
      toast.error("Signed out due to 30 minutes of inactivity.");
      await logout();
      // Bounce to login with a hint the URL layer can pick up.
      const nextParam = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?expired=1&next=${nextParam}`;
    }, IDLE_LIMIT_MS);
  }, [user, logout]);

  useEffect(() => {
    if (!user) {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (idleWarnTimer.current) clearTimeout(idleWarnTimer.current);
      return;
    }
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const handler = () => resetIdle();
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (idleWarnTimer.current) clearTimeout(idleWarnTimer.current);
    };
  }, [user, resetIdle]);

  return (
    <AuthCtx.Provider value={{
      user, loading, setupRequired,
      login, setup, logout,
      submitTwoFactorChallenge, startTwoFactorSetup, verifyTwoFactorSetup,
      refresh: bootstrap,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
